import { Injectable } from '@angular/core';
import { Observable, defer, iif, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Doctor, FilterType, NameFormat } from 'src/API';
import { Patient } from '../../core/api/patient.service';
import { FilterService } from './../../filter/filter.service';
import { PersonNamePipe } from '../shared-pipes/person-name.pipe';

/** A patient plus everything a picker row renders or matches on. */
export interface SearchablePatient {
  patient: Partial<Patient>;
  fullName: string;
  /** Lowercased name + health card + email + phone, so one `includes` covers every search field. */
  patientDataToSearch: string;
  /** ISO timestamp a "recent patients" ordering can be based on. */
  lastTouchedAt: string;
}

/** Editing a patient is as good a signal of "recent" as creating one. */
export function byMostRecentlyTouched(a: SearchablePatient, b: SearchablePatient): number {
  return b.lastTouchedAt.localeCompare(a.lastTouchedAt);
}

/**
 * The patient list behind every patient picker: one fetch, one searchable shape and one definition
 * of what counts as a match, so pickers only differ in how they look.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientSearchService {
  constructor(private personNamePipe: PersonNamePipe, private filterService: FilterService) {}

  /** Every patient of the doctor's clinic, in the order the API returns them. */
  searchablePatients(doctor: Doctor): Observable<SearchablePatient[]> {
    return iif(
      () => !!doctor && !!doctor.clinic,
      defer(() => this.filterService.queryFilterData(FilterType.patient, null)),
      of([])
    ).pipe(map((patients: Partial<Patient>[]) => this.toSearchablePatients(patients || [])));
  }

  toSearchablePatients(patients: Partial<Patient>[]): SearchablePatient[] {
    return patients.map(patient => this.toSearchablePatient(patient));
  }

  normalizeSearchTerm(term: string): string {
    return term
      ? term
          .toLocaleLowerCase()
          .trim()
          .replace(/\s+/g, ' ')
      : term;
  }

  /** The matches for an already-normalized term, closest match first. */
  matching(searchTerm: string, patients: SearchablePatient[]): SearchablePatient[] {
    if (!searchTerm) {
      return patients;
    }

    return patients
      .filter(searchable => searchable.patientDataToSearch.includes(searchTerm))
      .sort(
        (a, b) =>
          a.patientDataToSearch.indexOf(searchTerm) - b.patientDataToSearch.indexOf(searchTerm)
      );
  }

  private toSearchablePatient(patient: Partial<Patient>): SearchablePatient {
    const fullName = this.personNamePipe.transform(patient as Patient, NameFormat.Fullname);
    const healthCard = patient.healthCard;

    return {
      patient,
      fullName,
      patientDataToSearch: (
        fullName +
        ((healthCard && healthCard.province + ' ' + healthCard.number) || '') +
        (patient.email || '') +
        (patient.phone || '')
      ).toLowerCase(),
      // Records predating either timestamp sort last rather than being dropped from a list.
      lastTouchedAt: patient['updatedAt'] || patient['createdAt'] || ''
    };
  }
}
